import type {
  BodyShape,
  CarriedAccessory,
  CharacterAgeGroup,
  CharacterStature,
  EyeColour,
  FaceShape,
  HairColour,
  Outerwear,
} from "../sim/appearance.ts";

export interface OuterwearSilhouetteConfig {
  readonly torso: {
    readonly width: number;
    readonly height: number;
    readonly depth: number;
  };
  readonly garment: {
    readonly width: number;
    readonly height: number;
    readonly depth: number;
  };
  readonly sleeveWidth: number;
}

export const APPEARANCE_PRESENTATION_CONFIG = Object.freeze({
  faceScaleByShape: {
    round: { x: 1.06, y: 1, z: 1.02 },
    oval: { x: 0.94, y: 1.1, z: 0.96 },
    angular: { x: 1.01, y: 0.98, z: 0.94 },
  } satisfies Readonly<Record<FaceShape, Readonly<{
    x: number;
    y: number;
    z: number;
  }>>>,
  bodyScaleByShape: {
    slender: {
      shoulder: 0.86,
      torso: 0.86,
      hip: 0.84,
      limb: 0.92,
      depth: 0.84,
      belly: 0,
    },
    balanced: {
      shoulder: 1,
      torso: 1,
      hip: 1,
      limb: 1,
      depth: 1,
      belly: 0,
    },
    broad: {
      shoulder: 1.13,
      torso: 1.09,
      hip: 1.1,
      limb: 1.08,
      depth: 1.14,
      belly: 0.65,
    },
  } satisfies Readonly<Record<BodyShape, Readonly<{
    shoulder: number;
    torso: number;
    hip: number;
    limb: number;
    depth: number;
    belly: number;
  }>>>,
  agePresentationById: {
    young_adult: {
      headScale: 1.015,
      eyeScale: 1.04,
      postureRadians: -0.006,
      cadenceScale: 1.06,
    },
    adult: {
      headScale: 1,
      eyeScale: 1,
      postureRadians: 0,
      cadenceScale: 1,
    },
    older_adult: {
      headScale: 0.985,
      eyeScale: 0.94,
      postureRadians: 0.065,
      cadenceScale: 0.88,
    },
  } satisfies Readonly<Record<CharacterAgeGroup, Readonly<{
    headScale: number;
    eyeScale: number;
    postureRadians: number;
    cadenceScale: number;
  }>>>,
  statureScaleById: {
    short: 0.94,
    average: 1,
    tall: 1.055,
  } satisfies Readonly<Record<CharacterStature, number>>,
  outerwearSilhouetteById: {
    wool_coat: {
      torso: { width: 1.02, height: 1.04, depth: 1.02 },
      garment: { width: 0.97, height: 1, depth: 1 },
      sleeveWidth: 1,
    },
    field_jacket: {
      torso: { width: 1.05, height: 0.94, depth: 1.04 },
      garment: { width: 0.98, height: 1, depth: 1.03 },
      sleeveWidth: 1.04,
    },
    cardigan: {
      torso: { width: 0.94, height: 0.98, depth: 0.94 },
      garment: { width: 0.92, height: 1, depth: 0.94 },
      sleeveWidth: 0.88,
    },
    raincoat: {
      torso: { width: 1.04, height: 1.04, depth: 1.05 },
      garment: { width: 1.04, height: 1.06, depth: 1.04 },
      sleeveWidth: 1.06,
    },
    track_jacket: {
      torso: { width: 1.02, height: 0.92, depth: 1.02 },
      garment: { width: 0.96, height: 1, depth: 0.98 },
      sleeveWidth: 0.98,
    },
    suit_jacket: {
      torso: { width: 0.98, height: 1.01, depth: 0.94 },
      garment: { width: 0.94, height: 1, depth: 0.94 },
      sleeveWidth: 0.9,
    },
    hoodie: {
      torso: { width: 1.07, height: 0.98, depth: 1.06 },
      garment: { width: 1.02, height: 1, depth: 1.06 },
      sleeveWidth: 1.04,
    },
    quilted_coat: {
      torso: { width: 1.08, height: 1.04, depth: 1.08 },
      garment: { width: 1.04, height: 1.06, depth: 1.06 },
      sleeveWidth: 1.08,
    },
    denim_jacket: {
      torso: { width: 1.04, height: 0.93, depth: 1.01 },
      garment: { width: 0.98, height: 1, depth: 0.98 },
      sleeveWidth: 0.98,
    },
  } satisfies Readonly<Record<Outerwear, OuterwearSilhouetteConfig>>,
  carriedPlacementById: {
    none: { x: 0.36, y: 0.89, z: -0.03, scaleX: 1, scaleY: 1, scaleZ: 1, restingYOffset: 0 },
    satchel: { x: 0.36, y: 0.9, z: -0.03, scaleX: 1, scaleY: 1, scaleZ: 1, restingYOffset: -0.22 },
    tote: { x: 0.37, y: 0.83, z: -0.025, scaleX: 0.9, scaleY: 1.08, scaleZ: 0.76, restingYOffset: -0.18 },
    backpack: { x: 0, y: 1.23, z: -0.3, scaleX: 1, scaleY: 1, scaleZ: 1, restingYOffset: -0.18 },
    briefcase: { x: 0.39, y: 0.74, z: -0.01, scaleX: 1, scaleY: 1, scaleZ: 1, restingYOffset: -0.12 },
    shopping_bag: { x: 0.39, y: 0.76, z: -0.02, scaleX: 1, scaleY: 1, scaleZ: 0.82, restingYOffset: -0.14 },
    cane: { x: 0.38, y: 0.69, z: 0.02, scaleX: 1, scaleY: 1, scaleZ: 1, restingYOffset: 0 },
    folio: { x: 0.36, y: 0.78, z: -0.01, scaleX: 1, scaleY: 1, scaleZ: 1, restingYOffset: -0.12 },
    camera: { x: 0, y: 1.26, z: 0.29, scaleX: 1, scaleY: 1, scaleZ: 1, restingYOffset: -0.18 },
    produce_basket: { x: 0.39, y: 0.72, z: -0.01, scaleX: 1, scaleY: 1, scaleZ: 1, restingYOffset: -0.08 },
    tool_roll: { x: 0.38, y: 0.78, z: -0.02, scaleX: 1, scaleY: 1, scaleZ: 1, restingYOffset: -0.12 },
    stroller: { x: 0.48, y: 0.43, z: 0.18, scaleX: 1, scaleY: 1, scaleZ: 1, restingYOffset: 0 },
    dog_lead: { x: 0.34, y: 0.87, z: 0.03, scaleX: 1, scaleY: 1, scaleZ: 1, restingYOffset: 0 },
    waist_pack: { x: 0, y: 1.09, z: 0.25, scaleX: 1, scaleY: 1, scaleZ: 1, restingYOffset: -0.08 },
  } satisfies Readonly<Record<CarriedAccessory, Readonly<{
    x: number;
    y: number;
    z: number;
    scaleX: number;
    scaleY: number;
    scaleZ: number;
    restingYOffset: number;
  }>>>,
  skinToneById: {
    porcelain: 0xd5b69d,
    fair: 0xc7a184,
    warm: 0xb68f72,
    olive: 0x9c775d,
    deep: 0x684a3b,
  } satisfies Readonly<
    Record<import("../sim/appearance.ts").SkinTone, number>
  >,
  hairColourById: {
    flax: 0xb9a46f,
    ash: 0x72685a,
    chestnut: 0x5d3d32,
    black: 0x272927,
    auburn: 0x754535,
    silver: 0xaaa99f,
  } satisfies Readonly<Record<HairColour, number>>,
  eyeColourById: {
    grey: 0x6f7b7c,
    blue: 0x5f7887,
    green: 0x647867,
    hazel: 0x7b6848,
    brown: 0x4d372d,
  } satisfies Readonly<Record<EyeColour, number>>,
} as const);
